import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormHeader,
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from './FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { selectAdvisorAdminPortalState } from '@/model/selectors';
// You'll need to create this action in your model
import { postAdvisorReportingAttributeByAdvisorIdRequest } from '@/model/advisorAdminPortalModel';
import { FormSelect } from './FormComponents';

const schema = z
  .object({
    advisorId: z
      .string()
      .min(1, 'Advisor ID is required')
      .regex(
        /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{3}$/,
        'Advisor ID must be in format "xxxx-xxx" (4 alphanumeric characters, hyphen, 3 alphanumeric characters)',
      ),
    attributeLabel: z.string().min(1, 'Attribute label is required'),
    attributeValue: z.string().optional(),
    action: z.enum(['create', 'update', 'delete'], {
      required_error: 'Please select an action',
    }),
  })
  .refine(
    data => {
      // Require attributeValue for create and update actions
      if (data.action === 'create' || data.action === 'update') {
        return !!data.attributeValue;
      }
      return true;
    },
    {
      message: 'Attribute value is required for create and update actions',
      path: ['attributeValue'],
    },
  );

export function AdminAdvisorReportingAttributesForm() {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      action: 'create',
    },
  });

  const { status, errorMessage } = useSelector(selectAdvisorAdminPortalState);

  // Watch the action field to conditionally handle attribute value
  const selectedAction = watch('action');

  // Clear attribute value when action changes to delete
  useEffect(() => {
    if (selectedAction === 'delete') {
      setValue('attributeValue', '');
    }
  }, [selectedAction, setValue]);

  const onSubmit = (data: z.infer<typeof schema>) => {
    dispatch(
      postAdvisorReportingAttributeByAdvisorIdRequest({
        ...data,
        attributeValue: data.attributeValue ?? undefined,
      }),
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 text-gray-700"
    >
      <div className="mb-4 space-y-2">
        <FormHeader
          title="Manage Advisor Attributes"
          className="mb-0"
        />
        <p className="text-gray-700">Add or update advisor reporting attributes.</p>
      </div>

      <FormSelect
        label="Action"
        id="action"
        control={control}
        name="action"
        error={errors.action}
        required={true}
        options={[
          { value: 'create', label: 'Create' },
          { value: 'update', label: 'Update' },
          { value: 'delete', label: 'Delete' },
        ]}
        placeholder="Select an action"
      />

      <FormInput
        label="Advisor ID"
        id="advisorId"
        type="text"
        placeholder="Enter advisor ID"
        error={errors.advisorId}
        register={register('advisorId')}
        required={true}
      />

      <FormInput
        label="Attribute Label"
        id="attributeLabel"
        type="text"
        placeholder="Enter attribute label"
        error={errors.attributeLabel}
        register={register('attributeLabel')}
        required={true}
      />

      <FormInput
        label="Attribute Value"
        id="attributeValue"
        type="text"
        placeholder="Enter attribute value"
        error={errors.attributeValue}
        register={register('attributeValue')}
        required={selectedAction === 'create' || selectedAction === 'update'}
        disabled={selectedAction === 'delete'}
      />

      <PrimarySecondaryButtonContainer>
        <SubmitButton
          label={`${selectedAction.charAt(0).toUpperCase() + selectedAction.slice(1)} attribute`}
          className="translate-y-4"
          isLoading={status === 'loading'}
          destructive={selectedAction === 'delete'}
        />
      </PrimarySecondaryButtonContainer>

      <FormErrorAlert
        mainErrorMessage={errorMessage ?? ''}
        className="translate-y-4"
      />
    </form>
  );
}
